// src/store/slices/permissionSlice.ts
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import PermissionService, {
  type Permission,
  type PermissionsResponse,
} from "@/services/api/permission";

const name = "permission";

// ✅ State interface
export interface PermissionState {
  totalPermissions: number;
  permissions: Permission[];
  isFetching: boolean;
  error?: string | null;
}

const initialState: PermissionState = {
  totalPermissions: 0,
  permissions: [],
  isFetching: false,
  error: null,
};

// ✅ Async thunk
export const getPermissions = createAsyncThunk<
  PermissionsResponse, // Return type
  Record<string, any> | undefined, // Params type
  { rejectValue: string } // Rejection type
>(`${name}/getPermissions`, async (params, { rejectWithValue }) => {
  try {
    const data = await PermissionService.getPermissions(params);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message ?? "Failed to fetch permissions");
  }
});

// ✅ Slice
const permissionSlice = createSlice({
  name,
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPermissions.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(
        getPermissions.fulfilled,
        (state, action: PayloadAction<PermissionsResponse>) => {
          state.isFetching = false;
          state.permissions = action.payload.rows;
          state.totalPermissions = action.payload.count;
        }
      )
      .addCase(getPermissions.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload ?? "Unknown error";
        console.error("Failed to fetch permissions:", action.payload);
      });
  },
});

export default permissionSlice.reducer;
export const { clear } = permissionSlice.actions;
